// Step-up: ask for the authenticator code at the moment it is needed
// (Sahil, 2026-10-24).
//
// A trusted device signs in without a code, so its session is aal1. The
// routes that open the bank or reveal stored logins still need aal2 and say
// so with 403 { code: "mfa_required" }. withStepUp() turns that into a code
// prompt (StepUpHost in components/SecondStep.js) and, once the code is
// accepted, runs the request again with the new, aal2 token.
//
// Imports nothing: the Supabase client is passed in, so tests run it as-is.

let handler = null;

/** StepUpHost registers itself here; null when it unmounts. */
export function setStepUpHandler(fn) { handler = typeof fn === "function" ? fn : null; }

/** Show the code prompt. Resolves true once a code is accepted, false if cancelled
 *  (or when no prompt is mounted, e.g. outside the signed-in app). */
export async function requestStepUp(reason = "") {
  if (!handler) return false;
  try { return !!(await handler(reason)); } catch (_) { return false; }
}

/** Is this response the server asking for the code? */
export function needsStepUp(status, body) {
  return status === 403 && !!body && body.code === "mfa_required";
}

/** Run a request that needs a verified session. send(accessToken) must
 *  return a fetch Response. On "mfa_required" the code prompt opens; after
 *  a good code the request is sent once more with the fresh token. Returns
 *  { res, data, cancelled }. */
export async function withStepUp(sb, send, reason = "") {
  const tokenNow = async () => (await sb.auth.getSession())?.data?.session?.access_token || "";
  let res = await send(await tokenNow());
  let data = await res.clone().json().catch(() => ({}));
  if (!needsStepUp(res.status, data)) return { res, data, cancelled: false };
  const ok = await requestStepUp(reason);
  if (!ok) return { res, data, cancelled: true };
  res = await send(await tokenNow());
  data = await res.clone().json().catch(() => ({}));
  return { res, data, cancelled: false };
}
